import { BellOff, Calendar, ChevronDown, Forum, Hash, HeadphoneOff, Lock, MicOff, PickRoles, Plus, Settings, Stoke, UserPlus, Video, Volume2, VolumeX } from '@/components/ui/icons';
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Link, useMatch } from 'react-router';
import { isPrivateChannel, serverPerks, type CategoryDTO, type ChannelDTO, type ReorderChannelsInput, type VoiceParticipantDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { serverApi } from '@/api/endpoints';
import { Avatar, CountBadge } from '@/components/ui/Avatar';
import { Menu, Tooltip, type MenuEntry } from '@/components/ui/Popover';
import { dragKindOf, isAfter, reorderIds, startDrag, type DragKind } from '@/lib/dragReorder';
import { useChannelPermissions, useMyPermissions } from '@/lib/permissions';
import { isChannelUnread, setChannelMuted } from '@/lib/readState';
import { formatEventTime } from '@/lib/time';
import { sidebarGroups, type SidebarGroup } from '@clanfire/shared';
import { cn } from '@/lib/utils';
import { voiceManager } from '@/realtime/voice';
import { useVoice } from '@/stores/realtime';
import { useMemberName, useServerCategories, useServerChannels, useServerMembers, useServers } from '@/stores/servers';
import { useReadStates } from '@/stores/social';
import { toast, useUi } from '@/stores/ui';
import { usePresence, useUser } from '@/stores/users';
import { useVoiceSettings } from '@/stores/voiceSettings';
import { VoiceUserMenu } from '@/components/voice/VoiceUserMenu';
import { useServerMenuItems } from './ServerRail';
import { isHappening, sortEvents, useEvents } from '@/stores/events';

/* ------------------------------------------------------------------ */
/* Collapsed categories (per device)                                    */
/* ------------------------------------------------------------------ */

const COLLAPSED_KEY = 'clanfire:collapsed-categories';

function useCollapsedCategories() {
  const [collapsed, setCollapsed] = useState<Record<string, true>>(() => {
    try {
      return JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? '{}') as Record<string, true>;
    } catch {
      return {};
    }
  });
  const toggle = useCallback((categoryId: string) => {
    setCollapsed((prev) => {
      const next = { ...prev };
      if (next[categoryId]) delete next[categoryId];
      else next[categoryId] = true;
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  return [collapsed, toggle] as const;
}

/* ------------------------------------------------------------------ */
/* Drag and drop                                                        */
/* ------------------------------------------------------------------ */

type DropSpot = { kind: 'channel'; id: string; after: boolean } | { kind: 'category'; id: string; after: boolean; into: boolean };

interface SidebarDnd {
  drag: { kind: DragKind; id: string } | null;
  spot: DropSpot | null;
  dragProps: (kind: 'channel' | 'category', id: string) => object;
  channelDropProps: (channel: ChannelDTO) => object;
  categoryDropProps: (category: CategoryDTO) => object;
}

/** Applies a layout optimistically and rolls it back if the server refuses. */
async function commitLayout(serverId: string, layout: ReorderChannelsInput) {
  const state = useServers.getState();
  const previous: ReorderChannelsInput = {
    channels: layout.channels.map((c) => ({ id: c.id, position: state.channels[c.id]!.position, categoryId: state.channels[c.id]!.categoryId })),
    categories: layout.categories.map((c) => ({ id: c.id, position: state.categories[c.id]!.position })),
  };
  state.applyLayout(layout);
  try {
    await serverApi.setLayout(serverId, layout);
  } catch (err) {
    useServers.getState().applyLayout(previous);
    toast.error(errorMessage(err, 'Could not move that.'));
  }
}

const NO_DND: SidebarDnd = { drag: null, spot: null, dragProps: () => ({}), channelDropProps: () => ({}), categoryDropProps: () => ({}) };

function useSidebarDnd(serverId: string, groups: SidebarGroup[], categories: CategoryDTO[], enabled: boolean): SidebarDnd {
  const [drag, setDrag] = useState<SidebarDnd['drag']>(null);
  const [spot, setSpot] = useState<DropSpot | null>(null);
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;
  if (!enabled) return NO_DND;

  const end = () => {
    setDrag(null);
    setSpot(null);
  };

  const moveChannel = (dragId: string, target: DropSpot) => {
    const dragged = useServers.getState().channels[dragId];
    if (!dragged) return;
    let categoryId: string | null;
    let list: ChannelDTO[];
    let index: number;
    if (target.kind === 'category') {
      // Dropped on a category header: first channel in that category.
      categoryId = target.id;
      list = (groupsRef.current.find((g) => g.category?.id === target.id)?.channels ?? []).filter((c) => c.id !== dragId);
      index = 0;
    } else {
      const group = groupsRef.current.find((g) => g.channels.some((c) => c.id === target.id));
      if (!group) return;
      categoryId = group.category?.id ?? null;
      list = group.channels.filter((c) => c.id !== dragId);
      index = list.findIndex((c) => c.id === target.id) + (target.after ? 1 : 0);
    }
    list.splice(index, 0, dragged);
    void commitLayout(serverId, { channels: list.map((c, position) => ({ id: c.id, position, categoryId })), categories: [] });
  };

  const moveCategory = (dragId: string, targetId: string, after: boolean) => {
    const ids = reorderIds(
      categoriesRef.current.map((c) => c.id),
      dragId,
      targetId,
      after,
    );
    void commitLayout(serverId, { channels: [], categories: ids.map((id, position) => ({ id, position })) });
  };

  return {
    drag,
    spot,
    dragProps: (kind, id) => ({
      draggable: true,
      onDragStart: (e: DragEvent) => {
        e.stopPropagation();
        startDrag(e, kind, id);
        setDrag({ kind, id });
      },
      onDragEnd: end,
    }),
    channelDropProps: (channel) => ({
      onDragOver: (e: DragEvent) => {
        if (dragKindOf(e) !== 'channel') return;
        e.preventDefault();
        e.stopPropagation();
        const after = isAfter(e);
        setSpot((s) => (s?.kind === 'channel' && s.id === channel.id && s.after === after ? s : { kind: 'channel', id: channel.id, after }));
      },
      onDrop: (e: DragEvent) => {
        if (dragKindOf(e) !== 'channel') return;
        const id = e.dataTransfer.getData('application/x-clanfire-channel');
        e.preventDefault();
        e.stopPropagation();
        const after = isAfter(e);
        end();
        if (id && id !== channel.id) moveChannel(id, { kind: 'channel', id: channel.id, after });
      },
    }),
    categoryDropProps: (category) => ({
      onDragOver: (e: DragEvent) => {
        const kind = dragKindOf(e);
        if (kind !== 'channel' && kind !== 'category') return;
        e.preventDefault();
        e.stopPropagation();
        const after = isAfter(e);
        const into = kind === 'channel';
        setSpot((s) => (s?.kind === 'category' && s.id === category.id && s.after === after && s.into === into ? s : { kind: 'category', id: category.id, after, into }));
      },
      onDrop: (e: DragEvent) => {
        const kind = dragKindOf(e);
        if (!kind) return;
        e.preventDefault();
        e.stopPropagation();
        const after = isAfter(e);
        end();
        if (kind === 'channel') {
          const id = e.dataTransfer.getData('application/x-clanfire-channel');
          if (id) moveChannel(id, { kind: 'category', id: category.id, after: false, into: true });
        } else if (kind === 'category') {
          const id = e.dataTransfer.getData('application/x-clanfire-category');
          if (id && id !== category.id) moveCategory(id, category.id, after);
        }
      },
    }),
  };
}

function DropLine({ after }: { after: boolean }) {
  return <span aria-hidden className={cn('pointer-events-none absolute inset-x-1 z-10 h-0.5 rounded-full bg-accent', after ? '-bottom-px' : '-top-px')} />;
}

/* ------------------------------------------------------------------ */
/* Sidebar                                                              */
/* ------------------------------------------------------------------ */

/** "38 online · 214 members", once the member list has loaded. */
export function useMemberCounts(serverId: string) {
  const members = useServerMembers(serverId);
  const loaded = useServers((s) => s.membersState[serverId] === 'loaded');
  // A large server's member list is partial (who's online), so the total comes from the server.
  const total = useServers((s) => s.servers[serverId]?.memberCount ?? 0);
  const presences = usePresence((s) => s.statuses);
  return useMemo(
    () => (loaded ? { total: Math.max(total, members.length), online: members.filter((m) => (presences[m.userId] ?? m.presence) !== 'offline').length } : null),
    [loaded, members, presences, total],
  );
}

/** "Pick your roles", when the server has roles members can give themselves. */
function PickRolesEntry({ serverId }: { serverId: string }) {
  const roles = useServers(useShallow((s) => Object.values(s.roles).filter((r) => r.serverId === serverId && r.selfAssignable && !r.isDefault)));
  const myRoles = useServers((s) => s.servers[serverId]?.me.roleIds);
  const openModal = useUi((s) => s.openModal);
  if (roles.length === 0) return null;
  const picked = roles.filter((r) => myRoles?.includes(r.id)).length;
  return (
    <button
      type="button"
      onClick={() => openModal({ type: 'pickRoles', serverId })}
      className="mb-2 flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm text-fg-soft transition-colors hover:bg-hover"
    >
      <PickRoles size={18} className="shrink-0 text-fg-muted" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold text-fg">Pick your roles</span>
        <span className="block truncate text-xs text-fg-muted">{picked ? `${picked} picked` : 'Choose what you’re into'}</span>
      </span>
    </button>
  );
}

/** The server's events: the next one (or the one happening now) and a way to see them all. */
function EventsEntry({ serverId }: { serverId: string }) {
  const record = useEvents((s) => s.byServer[serverId]);
  const loaded = useEvents((s) => !!s.loaded[serverId]);
  const canManage = useMyPermissions(serverId).can('MANAGE_EVENTS');
  const openModal = useUi((s) => s.openModal);
  const events = useMemo(() => sortEvents(record), [record]);

  useEffect(() => {
    if (!loaded) useEvents.getState().load(serverId).catch(() => undefined);
  }, [serverId, loaded]);

  if (!loaded || (events.length === 0 && !canManage)) return null;
  const next = events[0];
  const live = next && isHappening(next);
  return (
    <button
      type="button"
      onClick={() => openModal({ type: 'events', serverId, eventId: next?.id })}
      className={cn(
        'mb-2 flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm transition-colors hover:bg-hover',
        live ? 'bg-success/10 text-fg' : 'text-fg-soft',
      )}
    >
      <Calendar size={18} className={cn('shrink-0', live ? 'text-success' : 'text-fg-muted')} />
      <span className="min-w-0 flex-1">
        {next ? (
          <>
            <span className="block truncate font-semibold text-fg">{next.name}</span>
            <span className="block truncate text-xs text-fg-muted">{live ? 'Happening now' : formatEventTime(next)}</span>
          </>
        ) : (
          <span className="font-semibold">Events</span>
        )}
      </span>
      {events.length > 1 && <span className="shrink-0 rounded-full bg-sunken px-1.5 text-[11px] font-bold text-fg-muted">+{events.length - 1}</span>}
    </button>
  );
}

export function ChannelSidebar({ serverId }: { serverId: string }) {
  const server = useServers((s) => s.servers[serverId]);
  const channels = useServerChannels(serverId);
  const categories = useServerCategories(serverId);
  const perms = useMyPermissions(serverId);
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLButtonElement>(null);
  const items = useServerMenuItems(serverId);
  const [collapsed, toggleCollapsed] = useCollapsedCategories();
  const counts = useMemberCounts(serverId);

  const groups = useMemo(() => sidebarGroups(channels, categories), [channels, categories]);
  const canManage = perms.can('MANAGE_CHANNELS');
  const dnd = useSidebarDnd(serverId, groups, categories, canManage);

  // Members power names/avatars in voice rows, mentions and the member list. A large server's list
  // is partial (who was online) and gets no presence updates, so it's fetched afresh on each visit.
  useEffect(() => {
    const { members, servers, loadMembers } = useServers.getState();
    const listed = Object.keys(members[serverId] ?? {}).length;
    void loadMembers(serverId, listed > 0 && listed < (servers[serverId]?.memberCount ?? 0));
  }, [serverId]);

  if (!server) {
    return (
      <div className="flex-1 p-4 text-sm text-fg-muted" role="status">
        This server isn't available.
      </div>
    );
  }

  const rowProps: RowProps = { joinedAt: server.me.joinedAt, canManage, canInvite: perms.can('CREATE_INVITES'), dnd };

  return (
    <>
      <button
        ref={headerRef}
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className={cn(
          'group relative flex shrink-0 justify-between gap-2 overflow-hidden px-5 pt-5 pb-3 text-left',
          server.bannerUrl ? 'min-h-[132px] items-end text-white' : 'items-start text-fg',
        )}
      >
        {server.bannerUrl && (
          <>
            <img src={server.bannerUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/5" />
          </>
        )}
        <span className="relative min-w-0">
          <span className="line-clamp-2 font-display text-[22px] leading-tight break-words group-hover:text-accent">{server.name}</span>
          {counts && (
            <span className={cn('mt-1 flex items-center gap-1.5 text-xs', server.bannerUrl ? 'text-white/85' : 'text-fg-muted')}>
              <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-accent-2" />
              {counts.online} online · {counts.total} {counts.total === 1 ? 'member' : 'members'}
            </span>
          )}
          {server.stokeCount > 0 && (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#f97316] to-[#e11d48] px-2 py-0.5 text-[11px] font-bold text-white">
              <Stoke size={11} aria-hidden />
              {serverPerks(server.stokeCount).name ?? 'Stoked'} · {server.stokeCount} {server.stokeCount === 1 ? 'Stoke' : 'Stokes'}
            </span>
          )}
        </span>
        <ChevronDown size={18} className={cn('relative mt-1.5 shrink-0 transition-transform', server.bannerUrl ? 'text-white/85' : 'text-fg-muted', menuOpen && 'rotate-180')} />
      </button>
      <Menu open={menuOpen} onClose={() => setMenuOpen(false)} anchor={headerRef} placement="bottom-start" items={items} label="Server menu" />

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pt-1 pb-4">
        {server.description && <p className="mb-2 px-2.5 text-xs leading-relaxed text-fg-muted">{server.description}</p>}
        <EventsEntry serverId={serverId} />
        <PickRolesEntry serverId={serverId} />
        {channels.length === 0 && categories.length === 0 && <p className="px-2.5 py-1 text-xs text-fg-muted">No channels yet.</p>}
        {groups.map((group) =>
          group.category ? (
            <CategorySection
              key={group.category.id}
              category={group.category}
              channels={group.channels}
              collapsed={!!collapsed[group.category.id]}
              onToggle={() => toggleCollapsed(group.category!.id)}
              categories={categories}
              rowProps={rowProps}
            />
          ) : (
            <ul key="uncategorized" className="mb-4 flex flex-col gap-0.5" aria-label="Channels">
              {group.channels.map((c) => (
                <ChannelRow key={c.id} channel={c} {...rowProps} />
              ))}
            </ul>
          ),
        )}
      </div>
    </>
  );
}

type RowProps = { joinedAt: string; canManage: boolean; canInvite: boolean; dnd: SidebarDnd };

function ChannelRow({ channel, ...props }: { channel: ChannelDTO } & RowProps) {
  return channel.type === 'VOICE' ? <VoiceChannelRow channel={channel} {...props} /> : <TextChannelRow channel={channel} {...props} />;
}

function CategorySection({
  category,
  channels,
  collapsed,
  onToggle,
  categories,
  rowProps,
}: {
  category: CategoryDTO;
  channels: ChannelDTO[];
  collapsed: boolean;
  onToggle: () => void;
  categories: CategoryDTO[];
  rowProps: RowProps;
}) {
  const openModal = useUi((s) => s.openModal);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  // Still the active channel while one of its threads is open.
  const activeChannelId = useMatch({ path: '/app/:serverId/:channelId', end: false })?.params.channelId;
  const voiceChannelId = useVoice((s) => s.channelId);
  const { dnd, canManage } = rowProps;
  const spot = dnd.spot?.kind === 'category' && dnd.spot.id === category.id ? dnd.spot : null;
  // A collapsed category still shows where you are (current channel / connected voice).
  const visible = collapsed ? channels.filter((c) => c.id === activeChannelId || c.id === voiceChannelId) : channels;

  const index = categories.findIndex((c) => c.id === category.id);
  const move = (delta: -1 | 1) => {
    const target = categories[index + delta];
    if (!target) return;
    const ids = reorderIds(
      categories.map((c) => c.id),
      category.id,
      target.id,
      delta === 1,
    );
    void commitLayout(category.serverId, { channels: [], categories: ids.map((id, position) => ({ id, position })) });
  };
  const createHere = () => openModal({ type: 'createChannel', serverId: category.serverId, channelType: 'TEXT', categoryId: category.id });

  const menuItems: MenuEntry[] = [
    { label: collapsed ? 'Expand category' : 'Collapse category', onSelect: onToggle },
    canManage && 'separator',
    canManage && { label: 'Create channel', icon: <Plus size={16} />, onSelect: createHere },
    canManage && { label: 'Edit category', icon: <Settings size={16} />, onSelect: () => openModal({ type: 'category', serverId: category.serverId, categoryId: category.id }) },
    canManage && index > 0 && { label: 'Move up', onSelect: () => move(-1) },
    canManage && index < categories.length - 1 && { label: 'Move down', onSelect: () => move(1) },
  ];

  return (
    <section className="mb-3" aria-label={category.name}>
      <div
        {...dnd.categoryDropProps(category)}
        {...dnd.dragProps('category', category.id)}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        className={cn(
          'group relative flex items-center justify-between rounded-full pt-2.5 pr-1.5 pb-1.5 pl-2',
          spot?.into && 'bg-accent-soft ring-1 ring-accent',
          dnd.drag?.kind === 'category' && dnd.drag.id === category.id && 'opacity-50',
        )}
      >
        {spot && !spot.into && <DropLine after={spot.after} />}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="flex min-w-0 flex-1 items-center gap-1 text-left text-[11px] font-semibold tracking-[0.08em] text-fg-muted uppercase hover:text-fg-soft"
        >
          <ChevronDown size={12} className={cn('shrink-0 transition-transform', collapsed && '-rotate-90')} />
          <span className="truncate">{category.name}</span>
        </button>
        {canManage && (
          <Tooltip label="Create channel">
            <button type="button" onClick={createHere} aria-label={`Create channel in ${category.name}`} className="rounded p-0.5 text-fg-muted hover:text-fg">
              <Plus size={16} />
            </button>
          </Tooltip>
        )}
      </div>
      <Menu open={!!menu} onClose={() => setMenu(null)} anchor={menu ?? { x: 0, y: 0 }} placement="bottom-start" items={menuItems} label={`${category.name} options`} />
      <ul className="flex flex-col gap-0.5">
        {visible.map((c) => (
          <ChannelRow key={c.id} channel={c} {...rowProps} />
        ))}
        {!collapsed && channels.length === 0 && <li className="px-2 py-1 text-xs text-fg-muted">{canManage ? 'Drag channels here or press +.' : 'No channels yet.'}</li>}
      </ul>
    </section>
  );
}

function RowActions({ channelId, serverId, canManage, canInvite }: { channelId: string; serverId: string; canManage: boolean; canInvite: boolean }) {
  const openModal = useUi((s) => s.openModal);
  return (
    <span className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
      {canInvite && (
        <button
          type="button"
          aria-label="Invite people"
          title="Invite people"
          onClick={(e) => {
            e.preventDefault();
            openModal({ type: 'invite', serverId });
          }}
          className="rounded p-0.5 text-fg-muted hover:text-fg"
        >
          <UserPlus size={15} />
        </button>
      )}
      {canManage && (
        <button
          type="button"
          aria-label="Edit channel"
          title="Edit channel"
          onClick={(e) => {
            e.preventDefault();
            openModal({ type: 'channelSettings', channelId });
          }}
          className="rounded p-0.5 text-fg-muted hover:text-fg"
        >
          <Settings size={15} />
        </button>
      )}
    </span>
  );
}

/** A channel icon, with a small lock when the channel is private. */
function ChannelIcon({ channel }: { channel: ChannelDTO }) {
  const defaultRoleId = useServers((s) => Object.values(s.roles).find((r) => r.serverId === channel.serverId && r.isDefault)?.id);
  const Icon = channel.type === 'VOICE' ? Volume2 : channel.type === 'FORUM' ? Forum : Hash;
  if (!isPrivateChannel(channel.overwrites, defaultRoleId)) return <Icon size={16} className="shrink-0 opacity-75" />;
  return (
    <span className="relative shrink-0" title="Private channel">
      <Icon size={16} className="opacity-75" />
      <Lock size={10} aria-label="Private" className="absolute -right-1.5 -bottom-1" />
    </span>
  );
}

function TextChannelRow({ channel, joinedAt, canManage, canInvite, dnd }: { channel: ChannelDTO } & RowProps) {
  const active = !!useMatch({ path: `/app/${channel.serverId}/${channel.id}`, end: false });
  const read = useReadStates((s) => s.channels[channel.id]);
  const muted = !!read?.muted;
  const unread = !active && !muted && isChannelUnread(channel, read?.lastReadAt, joinedAt);
  const mentions = read?.mentionCount ?? 0;
  const spot = dnd.spot?.kind === 'channel' && dnd.spot.id === channel.id ? dnd.spot : null;
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);

  return (
    <li className="relative" {...dnd.channelDropProps(channel)}>
      {spot && <DropLine after={spot.after} />}
      <Link
        to={`/app/${channel.serverId}/${channel.id}`}
        aria-current={active ? 'page' : undefined}
        aria-label={unread ? `${channel.name}, unread` : muted ? `${channel.name}, muted` : undefined}
        {...dnd.dragProps('channel', channel.id)}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        className={cn(
          'group flex h-9 items-center gap-2.5 rounded-full px-3 transition-colors',
          active ? 'bg-surface font-bold text-fg' : unread ? 'font-bold text-fg hover:bg-hover' : 'text-fg-soft hover:bg-hover hover:text-fg',
          muted && !active && 'opacity-60',
          dnd.drag?.id === channel.id && 'opacity-50',
        )}
      >
        <ChannelIcon channel={channel} />
        <span className="flex-1 truncate text-sm">{channel.name}</span>
        <RowActions channelId={channel.id} serverId={channel.serverId} canManage={canManage} canInvite={canInvite} />
        {mentions > 0 ? <CountBadge count={mentions} /> : unread && <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-accent" />}
      </Link>
      <Menu
        open={!!menu}
        onClose={() => setMenu(null)}
        anchor={menu ?? { x: 0, y: 0 }}
        placement="bottom-start"
        items={[{ label: muted ? 'Unmute channel' : 'Mute channel', icon: <BellOff size={16} />, onSelect: () => void setChannelMuted(channel.id, !muted) }]}
        label={`#${channel.name} options`}
      />
    </li>
  );
}

function VoiceChannelRow({ channel, canManage, dnd }: { channel: ChannelDTO } & RowProps) {
  const canConnect = useChannelPermissions(channel.id).can('CONNECT');
  const active = !!useMatch({ path: `/app/${channel.serverId}/${channel.id}`, end: false });
  const participants = useVoice((s) => s.participants[channel.id]);
  const connectedHere = useVoice((s) => s.channelId === channel.id);
  const spot = dnd.spot?.kind === 'channel' && dnd.spot.id === channel.id ? dnd.spot : null;

  return (
    <li className="relative" {...dnd.channelDropProps(channel)}>
      {spot && <DropLine after={spot.after} />}
      <Link
        to={`/app/${channel.serverId}/${channel.id}`}
        onClick={() => {
          if (canConnect && !connectedHere) void voiceManager.join(channel.id, channel.serverId);
        }}
        aria-current={active ? 'page' : undefined}
        {...dnd.dragProps('channel', channel.id)}
        className={cn(
          'group flex h-9 items-center gap-2.5 rounded-full px-3 transition-colors',
          active ? 'bg-surface font-bold text-fg' : 'text-fg-soft hover:bg-hover hover:text-fg',
          connectedHere && 'font-semibold text-success',
          dnd.drag?.id === channel.id && 'opacity-50',
        )}
      >
        <ChannelIcon channel={channel} />
        <span className="flex-1 truncate text-sm">{channel.name}</span>
        <RowActions channelId={channel.id} serverId={channel.serverId} canManage={canManage} canInvite={false} />
      </Link>
      {participants && participants.length > 0 && (
        <ul className="mt-0.5 mb-1 ml-6 flex flex-col gap-0.5">
          {participants.map((p) => (
            <VoiceParticipantRow key={p.userId} participant={p} />
          ))}
        </ul>
      )}
    </li>
  );
}

function VoiceParticipantRow({ participant }: { participant: VoiceParticipantDTO }) {
  const user = useUser(participant.userId);
  const name = useMemberName(participant.serverId, participant.userId);
  const speaking = useVoice((s) => !!s.speaking[participant.userId]);
  const locallyMuted = useVoiceSettings((s) => !!s.localMutes[participant.userId]);
  const openModal = useUi((s) => s.openModal);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  return (
    <li>
      <button
        type="button"
        onClick={() => openModal({ type: 'profile', userId: participant.userId, serverId: participant.serverId })}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        title="Right-click for volume"
        className="flex w-full items-center gap-2 rounded-full px-1.5 py-1 text-left text-[13px] text-fg-soft hover:bg-hover"
      >
        <Avatar user={user} size={24} speaking={speaking && !participant.muted && !locallyMuted} />
        <span className="flex-1 truncate">{name ?? user?.displayName ?? 'Loading…'}</span>
        {locallyMuted && <VolumeX size={14} className="text-fg-muted" aria-label="Muted for you" />}
        {participant.screen && (
          <span className="rounded bg-danger px-1 text-[10px] leading-4 font-extrabold text-white" title="Sharing their screen">
            LIVE
          </span>
        )}
        {participant.video && <Video size={14} className="text-fg-muted" aria-label="Camera on" />}
        {participant.deafened ? (
          <HeadphoneOff size={14} className="text-danger" aria-label="Deafened" />
        ) : participant.muted ? (
          <MicOff size={14} className="text-danger" aria-label="Muted" />
        ) : null}
      </button>
      {menu && <VoiceUserMenu userId={participant.userId} serverId={participant.serverId} point={menu} onClose={() => setMenu(null)} />}
    </li>
  );
}
